import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Posizione GPS.
 *
 * Due modalità, entrambe scelte dalla persona:
 *
 *   * **su richiesta** — si chiede la posizione nel momento in cui serve
 *     (ordinare i luoghi per vicinanza, sapere quando partire da dove sei);
 *   * **continua** — l'app segue la posizione mentre giri, utile in viaggio.
 *
 * La posizione **non lascia il dispositivo**: serve a calcolare distanze nel
 * browser e a passare il punto di partenza al navigatore. Non viene inviata al
 * server, non viene salvata una cronologia. L'unica cosa memorizzata è la
 * preferenza «segui la mia posizione», in localStorage.
 */

const TRACKING_KEY = 'tappa.gpsTracking';

export interface Position {
  readonly lat: number;
  readonly lon: number;
  readonly accuracyM: number;
  readonly at: number;
}

export type GeoStatus = 'idle' | 'requesting' | 'granted' | 'denied' | 'unavailable' | 'error';

interface GeoState {
  readonly position: Position | null;
  readonly status: GeoStatus;
  readonly error: string | null;
  /** true se l'app sta seguendo la posizione in continuo. */
  readonly tracking: boolean;
  readonly supported: boolean;
  /** Chiede la posizione una volta sola. */
  readonly requestOnce: () => Promise<Position | null>;
  readonly setTracking: (enabled: boolean) => void;
}

const GeoContext = createContext<GeoState | null>(null);

function readStoredTracking(): boolean {
  try {
    return window.localStorage.getItem(TRACKING_KEY) === '1';
  } catch {
    return false;
  }
}

function describeError(error: GeolocationPositionError): { status: GeoStatus; message: string } {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return {
        status: 'denied',
        message: 'Non hai dato il permesso di usare la posizione. Puoi attivarlo dalle impostazioni del browser.',
      };
    case error.POSITION_UNAVAILABLE:
      return { status: 'unavailable', message: 'Non riesco a determinare dove sei in questo momento.' };
    case error.TIMEOUT:
      return { status: 'error', message: 'La posizione ci sta mettendo troppo. Riprova.' };
    default:
      return { status: 'error', message: 'Non riesco a leggere la posizione.' };
  }
}

export function GeolocationProvider({ children }: { children: ReactNode }): ReactNode {
  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  const [position, setPosition] = useState<Position | null>(null);
  const [status, setStatus] = useState<GeoStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [tracking, setTrackingState] = useState(() => (supported ? readStoredTracking() : false));
  const watchIdRef = useRef<number | null>(null);

  const toPosition = (raw: GeolocationPosition): Position => ({
    lat: raw.coords.latitude,
    lon: raw.coords.longitude,
    accuracyM: raw.coords.accuracy,
    at: raw.timestamp,
  });

  const requestOnce = useCallback(async (): Promise<Position | null> => {
    if (!supported) {
      setStatus('unavailable');
      setError('Questo dispositivo non espone la posizione.');
      return null;
    }

    setStatus('requesting');
    setError(null);

    return new Promise<Position | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (raw) => {
          const next = toPosition(raw);
          setPosition(next);
          setStatus('granted');
          resolve(next);
        },
        (caught) => {
          const described = describeError(caught);
          setStatus(described.status);
          setError(described.message);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 },
      );
    });
  }, [supported]);

  const setTracking = useCallback(
    (enabled: boolean) => {
      setTrackingState(enabled);
      try {
        if (enabled) window.localStorage.setItem(TRACKING_KEY, '1');
        else window.localStorage.removeItem(TRACKING_KEY);
      } catch {
        // Preferenza non memorizzabile: il tracciamento resta valido per la sessione.
      }
    },
    [],
  );

  // Tracciamento continuo: attivo solo finché la preferenza è accesa.
  useEffect(() => {
    if (!supported || !tracking) {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }

    setStatus('requesting');
    watchIdRef.current = navigator.geolocation.watchPosition(
      (raw) => {
        setPosition(toPosition(raw));
        setStatus('granted');
        setError(null);
      },
      (caught) => {
        const described = describeError(caught);
        setStatus(described.status);
        setError(described.message);
        // Permesso negato: si spegne da solo invece di riprovare all'infinito.
        if (described.status === 'denied') setTracking(false);
      },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [supported, tracking, setTracking]);

  const value = useMemo<GeoState>(
    () => ({ position, status, error, tracking, supported, requestOnce, setTracking }),
    [position, status, error, tracking, supported, requestOnce, setTracking],
  );

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
}

export function useGeolocation(): GeoState {
  const context = useContext(GeoContext);
  if (context === null) throw new Error('useGeolocation va usato dentro GeolocationProvider');
  return context;
}
